// CompareIndex.js
// The /compare landing page. Lists published comparison articles as cards and
// sets its own page-level SEO (title + meta + canonical + CollectionPage
// JSON-LD). Only renders comparisons where published === true.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompareLayout from './CompareLayout';
import { getPublishedComparisons, colors } from './compareData';

const SITE = 'https://teetimeorganizer.net';

function useIndexSeo(comparisons) {
  useEffect(() => {
    document.title = 'Compare Golf Group & League Software | Tee Time Organizer';

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.name = 'description';
      document.head.appendChild(desc);
    }
    desc.content =
      'How Tee Time Organizer compares to other golf group and league software — honest, side-by-side comparisons of features and pricing to help organizers choose.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE}/compare`);

    const id = 'compare-index-jsonld';
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Compare Golf Group & League Software',
      url: `${SITE}/compare`,
      hasPart: comparisons.map((c) => ({
        '@type': 'Article',
        headline: c.title,
        url: `${SITE}/compare/${c.slug}`,
      })),
    });
  }, [comparisons]);
}

export default function CompareIndex() {
  const navigate = useNavigate();
  const comparisons = getPublishedComparisons();
  useIndexSeo(comparisons);

  return (
    <CompareLayout maxWidth={920}>
      <div style={{ marginBottom: 32 }}>
        <h1
          style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: 38,
            fontWeight: 400,
            color: colors.gray900,
            margin: '0 0 10px',
            lineHeight: 1.15,
          }}
        >
          How Tee Time Organizer compares
        </h1>
        <p style={{ fontSize: 17, color: colors.gray600, lineHeight: 1.6, margin: 0, maxWidth: 640 }}>
          Honest, side-by-side comparisons with other golf group and league tools — what each does
          well, where they differ, and which kind of group each one fits. Based on publicly
          available information and kept current.
        </p>
      </div>

      {comparisons.length === 0 ? (
        <p style={{ color: colors.gray500 }}>Comparisons are coming soon.</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {comparisons.map((c) => (
            <article
              key={c.slug}
              onClick={() => navigate(`/compare/${c.slug}`)}
              style={{
                background: 'white',
                border: `1px solid ${colors.gray200}`,
                borderRadius: 14,
                padding: 22,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: colors.green700,
                  marginBottom: 8,
                }}
              >
                vs {c.competitor}
              </div>
              <h2
                style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: 21,
                  fontWeight: 400,
                  color: colors.gray900,
                  margin: '0 0 10px',
                  lineHeight: 1.25,
                }}
              >
                {c.title.replace(': How Tee Time Organizer Compares (2026)', '')}
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: colors.gray600,
                  lineHeight: 1.6,
                  margin: '0 0 16px',
                  flexGrow: 1,
                }}
              >
                {c.excerpt}
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.green700, fontWeight: 700 }}>Read comparison →</span>
                <span style={{ color: colors.gray400 }}>{c.readMinutes} min read</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </CompareLayout>
  );
}
